import './HvacResultSavingsOverall.scss';
import { EnergySource, HvacArea, HvacResults } from '@belimo-retrofit-portal/logic';
import clsx from 'clsx';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { CarbonSavingsUnit } from 'src/modules/common/components/CarbonSavingsUnit';
import { EnergyEfficiencyLabelWrapper } from 'src/modules/common/components/EnergyEfficiencyLabelWrapper';
import { EnergyEfficiencyStack } from 'src/modules/common/components/EnergyEfficiencyStack';
import { EnergySavingsUnit } from 'src/modules/common/components/EnergySavingsUnit';
import { FormattedMathMessage } from 'src/modules/common/components/FormattedMathMessage';
import { SavingsAmount } from 'src/modules/common/components/SavingsAmount';
import { SavingsValue } from 'src/modules/common/components/SavingsValue';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { ChevronRightIcon } from 'src/modules/common/icons/ChevronRightIcon';
import { CloudIcon } from 'src/modules/common/icons/CloudIcon';
import { CoolingIcon } from 'src/modules/common/icons/CoolingIcon';
import { FanIcon } from 'src/modules/common/icons/FanIcon';
import { HeatingIcon } from 'src/modules/common/icons/HeatingIcon';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { LightningIcon } from 'src/modules/common/icons/LightningIcon';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { BodyMedium02 } from 'src/modules/common/typography/BodyMedium02';
import { HvacFormData } from 'src/modules/hvac/types/HvacFormData';
import { HvacResultSavingsCard } from 'src/modules/hvac/views/HvacResultSavingsCard';

type Props = {
  readonly form: HvacFormData;
  readonly results: HvacResults;
};

export const HvacResultSavingsOverall = ({ form, results }: Props): React.ReactElement => {
  const heatingEnabled = true; // form.assessment.areas[HvacArea.HEATING].enabled;
  const coolingEnabled = true; // form.assessment.areas[HvacArea.COOLING].enabled;
  const ventilationEnabled = true; // form.assessment.areas[HvacArea.VENTILATION].enabled;

  return (
    <>
      <div className="bp-hvac-result-savings-overall">
        <div className="bp-hvac-result-savings-overall__energy-efficiency-card">
          <BodyMedium01>
            <FormattedMessage id="hvac/tab/areas"/>
          </BodyMedium01>

          <div className="bp-hvac-result-savings-overall__energy-efficiency-diagram">
            <div className="bp-hvac-result-savings-overall__row">
              <div className="bp-hvac-result-savings-overall__content"/>

              <div className="bp-hvac-result-savings-overall__assessment">
                <BodyMedium02 className="bp-hvac-result-savings-overall__assessment-tab">
                  <FormattedMessage id="hvac/form/common/actual"/>
                </BodyMedium02>

                <div className="bp-hvac-result-savings-overall__assessment-divider">&nbsp;</div>

                <BodyMedium02 className="bp-hvac-result-savings-overall__assessment-tab">
                  <FormattedMessage id="hvac/form/common/future"/>
                </BodyMedium02>
              </div>
            </div>

            <div
              className={clsx(
                'bp-hvac-result-savings-overall__row',
                heatingEnabled ? null : 'bp-hvac-result-savings-overall__row--disabled',
              )}
            >
              <div className="bp-hvac-result-savings-overall__content">
                <div className="bp-hvac-result-savings-overall__content-icon">
                  <HeatingIcon/>
                </div>

                <BodyMedium02>
                  <FormattedMessage id="hvac/tab/heating"/>
                </BodyMedium02>
              </div>

              <div className="bp-hvac-result-savings-overall__assessment">
                <div className="bp-hvac-result-savings-overall__assessment-label">
                  <EnergyEfficiencyLabelWrapper
                    size="sm"
                    fixed={true}
                    value={results.perArea[HvacArea.HEATING].rating.actual}
                    disabled={false}
                  />
                </div>

                <div className="bp-hvac-result-savings-overall__assessment-divider">
                  <ChevronRightIcon/>
                </div>

                <div className="bp-hvac-result-savings-overall__assessment-label">
                  <EnergyEfficiencyLabelWrapper
                    size="sm"
                    fixed={true}
                    value={results.perArea[HvacArea.HEATING].rating.future}
                    disabled={false}
                  />
                </div>
              </div>
            </div>

            <div
              className={clsx(
                'bp-hvac-result-savings-overall__row',
                coolingEnabled ? null : 'bp-hvac-result-savings-overall__row--disabled',
              )}
            >
              <div className="bp-hvac-result-savings-overall__content">
                <div className="bp-hvac-result-savings-overall__content-icon">
                  <CoolingIcon/>
                </div>

                <BodyMedium02>
                  <FormattedMessage id="hvac/tab/cooling"/>
                </BodyMedium02>
              </div>

              <div className="bp-hvac-result-savings-overall__assessment">
                <div className="bp-hvac-result-savings-overall__assessment-label">
                  <EnergyEfficiencyLabelWrapper
                    size="sm"
                    fixed={true}
                    value={results.perArea[HvacArea.COOLING].rating.actual}
                    disabled={false}
                  />
                </div>

                <div className="bp-hvac-result-savings-overall__assessment-divider">
                  <ChevronRightIcon/>
                </div>

                <div className="bp-hvac-result-savings-overall__assessment-label">
                  <EnergyEfficiencyLabelWrapper
                    size="sm"
                    fixed={true}
                    value={results.perArea[HvacArea.COOLING].rating.future}
                    disabled={false}
                  />
                </div>
              </div>
            </div>

            <div
              className={clsx(
                'bp-hvac-result-savings-overall__row',
                ventilationEnabled ? null : 'bp-hvac-result-savings-overall__row--disabled',
              )}
            >
              <div className="bp-hvac-result-savings-overall__content">
                <div className="bp-hvac-result-savings-overall__content-icon">
                  <FanIcon/>
                </div>

                <BodyMedium02>
                  <FormattedMessage id="hvac/tab/ventilation"/>
                </BodyMedium02>
              </div>

              <div className="bp-hvac-result-savings-overall__assessment">
                <div className="bp-hvac-result-savings-overall__assessment-label">
                  <EnergyEfficiencyLabelWrapper
                    size="sm"
                    fixed={true}
                    value={results.perArea[HvacArea.VENTILATION].rating.actual}
                    disabled={false}
                  />
                </div>

                <div className="bp-hvac-result-savings-overall__assessment-divider">
                  <ChevronRightIcon/>
                </div>

                <div className="bp-hvac-result-savings-overall__assessment-label">
                  <EnergyEfficiencyLabelWrapper
                    size="sm"
                    fixed={true}
                    value={results.perArea[HvacArea.VENTILATION].rating.future}
                    disabled={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bp-hvac-result-savings-overall__energy-efficiency-card">
          <BodyMedium01>
            <FormattedMessage id="hvac/tab/overall"/>
          </BodyMedium01>

          <div className="bp-hvac-result-savings-overall__energy-efficiency-diagram">
            <div className="bp-hvac-result-savings-overall__assessment">
              <BodyMedium02 className="bp-hvac-result-savings-overall__stack-tab">
                <FormattedMessage id="hvac/form/common/actual"/>
              </BodyMedium02>

              <div className="bp-hvac-result-savings-overall__assessment-divider">&nbsp;</div>

              <BodyMedium02 className="bp-hvac-result-savings-overall__stack-tab">
                <FormattedMessage id="hvac/form/common/future"/>
              </BodyMedium02>
            </div>

            <div className="bp-hvac-result-savings-overall__assessment">
              <div className="bp-hvac-result-savings-overall__assessment-stack">
                <EnergyEfficiencyStack value={results.overall.rating.actual}/>
              </div>

              <div className="bp-hvac-result-savings-overall__assessment-divider">&nbsp;</div>

              <div className="bp-hvac-result-savings-overall__assessment-stack">
                <EnergyEfficiencyStack value={results.overall.rating.future}/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bp-hvac-result-savings-overall__cards">
        <HvacResultSavingsCard
          icon={<HeatingIcon/>}
          title={<FormattedMessage id="hvac/result/savings/potential/thermalSavings/title"/>}
          label={<FormattedMessage id="hvac/result/savings/potential/thermalSavings/diagram/label"/>}
          value={(
            <>
              <SavingsValue
                value={results.overall.savings.thermal.relative?.mul(100) ?? null}
                precision={0}
              />
              <FormattedMessage id="unit/percent"/>
            </>
          )}
          firstDetailLabel={<FormattedMessage id="hvac/result/savings/potential/thermalSavings/heating"/>}
          firstDetailValue={(
            <>
              <SavingsValue
                value={results.perArea[HvacArea.HEATING].savings.thermal.absolute?.div(1_000) ?? null}
                precision={0}
              />

              <EnergySavingsUnit source={results.perArea[HvacArea.HEATING].savings.thermal.source}/>
            </>
          )}
          secondDetailLabel={<FormattedMessage id="hvac/result/savings/potential/thermalSavings/annual"/>}
          secondDetailValue={(
            <SavingsAmount
              value={results.overall.savings.thermal.annual}
              currency={form.currency}
            />
          )}
        />

        <HvacResultSavingsCard
          icon={<LightningIcon/>}
          title={<FormattedMessage id="hvac/result/savings/potential/electricalSavings/title"/>}
          label={<FormattedMessage id="hvac/result/savings/potential/electricalSavings/diagram/label"/>}
          value={(
            <>
              <SavingsValue
                value={results.overall.savings.electrical.relative?.mul(100) ?? null}
                precision={0}
              />

              <FormattedMessage id="unit/percent"/>
            </>
          )}
          firstDetailLabel={<FormattedMessage id="hvac/result/savings/potential/electricalSavings/energy"/>}
          firstDetailValue={(
            <>
              <SavingsValue
                value={results.overall.savings.electrical.absolute?.div(1_000) ?? null}
                precision={0}
              />
              <EnergySavingsUnit source={EnergySource.ELECTRICITY}/>
            </>
          )}
          secondDetailLabel={<FormattedMessage id="hvac/result/savings/potential/electricalSavings/annual"/>}
          secondDetailValue={(
            <SavingsAmount
              value={results.overall.savings.electrical.annual}
              currency={form.currency}
            />
          )}
        />

        <HvacResultSavingsCard
          headerPostfix={(
            <Tooltip
              id="bp-hvac-result-savings-formula"
              value={(
                <FormattedMathMessage
                  id="hvac/result/savings/potential/carbonDioxide/tooltip/content"
                  values={{ sub, sup }}
                />
              )}
            >
              <div
                className="bp-hvac-result-savings-overall__card-unit-icon"
              >
                <InfoIcon/>
              </div>
            </Tooltip>
          )}
          icon={<CloudIcon/>}
          title={<FormattedMessage id="hvac/result/savings/potential/carbonDioxide/title" values={{ sub }}/>}
          label={<FormattedMessage id="hvac/result/savings/potential/carbonDioxide/diagram/label" values={{ sub }}/>}
          value={(
            <>
              <SavingsValue
                value={results.overall.savings.carbon.relative?.mul(100) ?? null}
                precision={0}
              />

              <div className="bp-hvac-result-savings-overall__card-unit">
                <FormattedMessage id="unit/percent"/>

              </div>
            </>
          )}
          firstDetailLabel={(
            <FormattedMessage
              id="hvac/result/savings/potential/carbonDioxide/thermal"
              values={{ sub }}
            />
          )}
          firstDetailValue={(
            <>
              <SavingsValue
                value={results.overall.savings.carbon.thermal?.div(1_000_000) ?? null}
                precision={0}
              />

              <CarbonSavingsUnit/>
            </>
          )}
          secondDetailLabel={(
            <FormattedMessage
              id="hvac/result/savings/potential/carbonDioxide/electrical"
              values={{ sub }}
            />
          )}
          secondDetailValue={(
            <>
              <SavingsValue
                value={results.overall.savings.carbon.electrical?.div(1_000_000) ?? null}
                precision={0}
              />

              <CarbonSavingsUnit/>
            </>
          )}
        />
      </div>
    </>
  );
};
